import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getBootstrapData } from '../../actions';
import { FetchError, IBootstrap } from '../../models';
import { RootState } from '../rootReducer';

export interface IBootstrapState {
    hasRequiredPlan: boolean;
    isAdmin: boolean;
    isAllowed: boolean;
    isBootstrapping: boolean;
    error?: Error;
}

export const initialState: IBootstrapState = {
    hasRequiredPlan: false,
    isAdmin: false,
    isAllowed: false,
    isBootstrapping: true,
};

const name = 'bootstrap';

const bootstrapSlice = createSlice({
    initialState,
    name,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getBootstrapAction.pending, (state) => {
                state.isBootstrapping = true;
            })
            .addCase(getBootstrapAction.fulfilled, (state) => {
                state.hasRequiredPlan = true;
                state.isAdmin = true;
                state.isAllowed = true;
                state.isBootstrapping = false;
                state.error = undefined;
            })
            .addCase(getBootstrapAction.rejected, (state, { payload }) => {
                state.isBootstrapping = false;
                state.error = undefined;
                if (!payload) {
                    return;
                }

                if (payload.code === 401) {
                    state.isAllowed = false;
                } else if (payload.code === 403) {
                    if (payload.body) {
                        const body = payload.body;
                        const access = body.attributes || body;
                        state.isAdmin = access.isAdmin ? access.isAdmin : false;
                        state.isAllowed = true;
                        state.hasRequiredPlan = access.hasRequiredPlan ? access.hasRequiredPlan : false;
                    } else {
                        state.isAdmin = false;
                        state.isAllowed = true;
                        state.hasRequiredPlan = false;
                    }
                } else {
                    state.error = payload;
                }
            });
    },
});

export const getBootstrapAction = createAsyncThunk<IBootstrap, void, { state: RootState; rejectValue: FetchError }>(
    `${name}/getBootstrap`,
    async (_, { getState, rejectWithValue }) => {
        try {
            const state = getState();
            const installation = state.installation.installation;
            const user = state.user.user;

            const response = await getBootstrapData(user, installation);

            return response;
        } catch (error) {
            const err = error as FetchError;
            if (err.body) {
                err.body = await err.body;
            }
            return rejectWithValue(error);
        }
    }
);

export const {} = bootstrapSlice.actions;

export const bootstrapSelectors = {
    error: (state: RootState) => state.bootstrap.error,
    hasRequiredPlan: (state: RootState) => state.bootstrap.hasRequiredPlan,
    isAdmin: (state: RootState) => state.bootstrap.isAdmin,
    isAllowed: (state: RootState) => state.bootstrap.isAllowed,
    isBootstrapping: (state: RootState) => state.bootstrap.isBootstrapping,
};

export default bootstrapSlice.reducer;
